import { EditorView } from '@codemirror/view';
import * as state from './state.js';
import { initEditor } from './editor.js';
import { initPaneResize } from './pane-resize.js';
import { initShareLink, updateUrlParams, decompressYaml } from './share-link.js';
import { connectWebSocket } from './websocket.js';
import {
    sendYAMLToPreview,
    applyReviewWidth,
    scalePreviewContent,
    resizeSvgIframe,
    resetVariables,
    initPreview,
    initPreviewAssets,
} from './preview.js';
import {
    loadPlaygroundConfig,
    loadExamples,
    initGallery,
    applyFormatAndVarsFromParams,
} from './examples-gallery.js';
import { initDownload } from './download.js';
import { initAISidebar, chatAboutChart } from './ai-sidebar.js';
import { patchedUpdatePreview } from './celebration.js';
import { setDoc } from './editor-view.js';

// Register the celebration-patched updatePreview in state so renderViaHTTP
// (the HTTP fallback) also uses the patched version, not the raw module export.
state.setUpdatePreview(patchedUpdatePreview);

// Test-only handle for the Playwright e2e lane (libs/playground/tests/e2e/):
// EditorView.findFromDOM(dom) is the documented public API for recovering a
// view instance from its mounted element. Deliberately NOT named window.EditorView:
// a library-shaped global name invites production code to treat it as a real
// integration point. Not a CodeMirror facade either: no wrapper API is
// invented, just the real, unmodified CM6 class, reachable only under this
// unambiguously test-scoped name.
window.__playgroundE2E = { EditorView };

document.addEventListener('DOMContentLoaded', async () => {
    // Resolve fingerprinted preview asset URLs from the Vite manifest (prod) or
    // fall back to unhashed dev paths.  Awaited so the manifest is ready before
    // initEditor completes and the first render can fire.
    await initPreviewAssets();

    // Initialize editor and code preview first (other inits depend on state.editor)
    const { editor } = await initEditor(sendYAMLToPreview);

    // Initialize pane resize (depends on DOM, not editor content)
    initPaneResize(sendYAMLToPreview, scalePreviewContent, resizeSvgIframe);

    // Initialize share link / copy YAML buttons
    initShareLink();

    // Initialize download
    initDownload();

    // Initialize preview event listeners (format/theme/grid/margins selectors, etc.)
    initPreview();

    // Initialize examples gallery modal and example-selector change handler
    initGallery(sendYAMLToPreview, resetVariables, updateUrlParams);

    // Initialize AI sidebar
    initAISidebar();

    // Expose chatAboutChart globally for iframe postMessage
    window.chatAboutChart = chatAboutChart;

    // Read URL parameters
    const params = new URLSearchParams(window.location.search);
    const compressedYamlParam = params.get('y');
    const savedReviewWidth = window.localStorage.getItem(state.REVIEW_WIDTH_STORAGE_KEY);

    applyFormatAndVarsFromParams(params);

    // Migration: legacy "true"/"false" boolean → 1200px
    const reviewWidth = savedReviewWidth === 'true' ? '1200'
        : savedReviewWidth === 'false' ? ''
        : (savedReviewWidth || '');
    applyReviewWidth(reviewWidth);

    if (compressedYamlParam) {
        try {
            const yaml = decompressYaml(compressedYamlParam);
            setDoc(editor, yaml);
        } catch (e) {
            console.error('Error decoding compressed YAML:', e);
        }
    }

    // Load config then examples, then connect WebSocket
    loadPlaygroundConfig()
        .then(() => loadExamples());

    connectWebSocket(sendYAMLToPreview);
});
